<script setup>
import { computed, onMounted, ref, useId } from 'vue'

const props = defineProps({
    text: {
        type: [String, Boolean],
        default: false,
    },
    size: {
        type: [String, Number],
        default: '100%',
    },
    type: {
        type: [String, Boolean],
        default: false, // 'artist', 'show', 'channel', 'genre'
    },
    randomized: {
        type: Boolean,
        default: true,
    },
    tag: {
        type: String,
        default: 'span',
    },
})

const root = ref(null)
const color = ref('black')

// `useId` is stable across a server render and its hydration, so each placeholder
// keeps its own width instead of redrawing on every render.
const instanceId = useId()

function randomizeSize(inputSize, variance = 0.4) {
    let hash = 2166136261
    for (let i = 0; i < instanceId.length; i++) {
        hash = Math.imul(hash ^ instanceId.charCodeAt(i), 16777619)
    }
    // Ids are sequential, and a bare FNV of `v-0`/`v-1` lands them a hair apart.
    hash ^= hash >>> 15
    hash = Math.imul(hash, 2246822507)
    hash ^= hash >>> 13
    const fraction = (hash >>> 0) / 4294967296

    return inputSize * (fraction * variance + (1 - variance / 2))
}

const styleWidth = computed(() => {
    switch (props.type) {
    case 'artist':
        return `${randomizeSize(9)}em`
    case 'show':
        return `${randomizeSize(10)}em`
    case 'channel':
        return '6em'
    case 'genre':
        return `${randomizeSize(10)}em`
    default:
        break
    }
    return Number.isInteger(props.size)
        ? `${props.randomized ? randomizeSize(props.size) : props.size}em`
        : props.size
})

const shouldDisplay = computed(() => !props.text || props.text.length === 0)

onMounted(() => {
    color.value = window.getComputedStyle(root.value).getPropertyValue('color')
})
</script>

<template>
    <component
        :is="tag"
        ref="root"
    >
        {{ !shouldDisplay ? text : '' }}
        <span
            v-if="shouldDisplay"
            class="placeholder skeleton"
            :style="{ width: styleWidth, backgroundColor: shouldDisplay ? color : 'none' }"
        />
    </component>
</template>

<style lang="scss" scoped>
@import '@design';

.placeholder {
    display: inline-block;
    height: 1em;
    background: $c-text-disabled;
    opacity: 0.3;
}
</style>
